// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/livret.js — LES LEVIERS DU LIVRET D'ACCUEIL (CONTENU ÉDITORIAL).
//
// ══ LA FORME QUI REVIENT ═══════════════════════════════════════════════════
//
// Le livret est fait d'objets rangés dans des listes JSONB ou des tables :
// modules, POI d'alentours, contacts d'assistance, catégories, FAQ. Une même
// forme s'y répète — décrite ici UNE fois, appliquée ensuite sans la redire :
//
//   AJOUTER   — POST, l'objet nouveau apparaît en fin de liste.
//   MODIFIER  — PATCH (ou POST selon le handler, cf. note méthode), un champ
//               ou plusieurs changent sur un objet identifié par id/clé.
//   RÉORDONNER— POST, un tableau d'identifiants dans l'ordre voulu ; ce qui
//               en est absent retombe en tri automatique.
//   BASCULER  — POST/PATCH, un booléen change sur un objet (affiché/masqué,
//               épinglé/exclu) : le geste le plus réversible qui soit.
//   SUPPRIMER — quand il existe, c'est TOUJOURS une suppression LOGIQUE
//               (`manual_deleted:true` + `displayed_to_guest:false`), jamais
//               un DELETE SQL : c'est précisément ce qui la sort de E1 et lui
//               permet d'entrer ici. Les DELETE durs de cette famille (modules,
//               catégories, FAQ, photos) restent EXCLUS — cf. rapport.
//
// ⚠️ CE QUE DIMITRI VOIT APRÈS CLIC EST TOUJOURS DOUBLE : l'écran ADMIN
// (l'éditeur du logement) ET, pour tout ce qui est `enabled`/`published`/
// `displayed_to_guest`, LA PAGE VOYAGEUR elle-même. Le `constatable` de
// chaque levier le nomme explicitement quand c'est le cas — un contenu
// « publié » n'est pas vu que par Dimitri.
//
// ⚠️ AUTO-TRADUCTION AU SAVE (ADR-200, CLAUDE.md § Admin Livret) : les champs
// `livret_config` et modules déclenchent, CÔTÉ ÉCRAN (pas ici), une traduction
// FR→EN/ES pour les langues VIDES au moment du save — jamais un écrasement
// d'une langue déjà remplie. Un geste ici qui pose du FR peut donc, sans que
// ce module le décrive, faire apparaître de l'EN/ES à côté : le `constatable`
// le mentionne pour les leviers texte (vague 9).
//
// ⚠️ MÉTHODE HTTP : ce fichier ne décrit QUE l'intention (endpoint/action/
// args), comme le veut `_forme.js`. Mais certains handlers (`livret_module_
// upsert`, `livret_config_upsert`, `livret_faq_upsert`) dispatchent sur
// `req.method` et refusent tout verbe hors POST/PATCH/DELETE attendu — un
// simple POST ne suffit pas partout (contrairement aux contacts d'assistance,
// qui acceptent POST en toutes circonstances). Le champ `methode`, ajouté à
// ces entrées, n'est PAS dans `CHAMPS_REQUIS` : c'est une note à l'usage de
// qui câblera l'exécution réelle, pas un champ vérifié par `_forme.js`.
//
// ⚠️ ARGUMENTS EN OBJET/TABLEAU (JSONB) : `lireGeste` (operateur-geste.js)
// aujourd'hui ne laisse passer que string/number/boolean — un objet ou un
// tableau dans les args ferait échouer la composition PAR LE MODÈLE. Plusieurs
// leviers ci-dessous (poi, contact, question/answer, wifi_networks…) ont
// nécessairement des arguments en objet : ils sont TOUS classés vague 9 pour
// cette raison-là ou celle du texte, et resteront des cartes vides tant que
// l'exécution vague 9 (où Dimitri complète lui-même) n'est pas construite.
// Décrire un « comment on ferait » n'est pas la même chose que fabriquer une
// carte utilisable aujourd'hui — ce n'est pas mon sujet de le construire.

// ══ CE QUE LA CARTE MONTRE AVANT LE CLIC — LES SEPT APERÇUS (2026-09-16) ═══
//
// Dimitri : « ce qui change, valeur par valeur, ancienne → nouvelle ». Les 26
// leviers de cette famille écrasaient un texte, un ordre, un drapeau ou un
// contact sans que rien ne montre ce qui était là. Chacun déclare désormais un
// `apercu` qui nomme UNE des sept actions de lecture de
// `lib/livret-lectures-handlers.js` (admin seul, zéro écriture, bornée) :
//
//   livret_module_lire         · 7 leviers (rubriques, photo, prérempli, copie)
//   livret_assistance_lire     · 5 leviers (contacts d'assistance)
//   livret_alentours_lire      · 4 leviers (lieux recommandés)
//   livret_categories_lire     · 4 leviers (catégories)
//   livret_config_lire         · 2 leviers (publier, éditer la config)
//   livret_faq_lire            · 2 leviers (questions fréquentes)
//   livret_sections_auto_lire  · 2 leviers (sections automatiques)
//
// ⚠️ `depend_de` NE PEUT CITER QUE DES ARGUMENTS DU LEVIER. C'est ce qui
// explique les entrées qui surprennent : « bascule cette rubrique » ne connaît
// que `id`, « attache une photo » que `module_id`, « copie les rubriques » que
// `target_logement_id`. Les lectures acceptent donc ces noms-là — une lecture
// qui n'aurait accepté que `logement_id` aurait laissé ces gestes GRIS pour
// toujours (`executerGeste` refuse de partir sans le jeton de l'aperçu).
//
// Reste `livret_override_set`, seul `apercu_absent` de la famille : il porte
// sur une RÉSERVATION (quel voyageur, quelles dates, pourquoi l'accès est
// bloqué), pas sur le contenu du livret — sa lecture est d'une autre nature.

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_LIVRET = Object.freeze({
  // ── Config du livret (livret_config) ──────────────────────────────────
  //
  // livret_config_upsert sert DEUX gestes très différents par leurs enjeux :
  // publier/dépublier (booléen pur, vague 1) et éditer le contenu (texte que
  // Dimitri écrit, vague 9). Deux entrées, un seul `action` — args disjoints,
  // c'est la garde anti-choix qui empêche de mélanger les deux dans un clic.
  // ⚠️ `livret_config_publier` — MARQUAGE `sortant` AJOUTÉ le 2026-09-09, à
  // l'ouverture de la vague 1. Publier ne change pas seulement un badge dans
  // l'éditeur : la page devient accessible au VOYAGEUR, par son lien et son QR.
  // C'est la définition même de « ça sort de chez FBZ ». Dépublier est
  // inoffensif, publier ne l'est pas — et un levier ne sait pas faire une
  // seule des deux directions (`args_autorises` filtre les clés, pas les
  // valeurs). La carte doit donc le dire dans les deux cas.

  livret_config_publier: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_config_upsert",
    methode: "PATCH",
    args_autorises: ["logement_id", "published"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Publier ou dépublier un livret",
    constatable:
      "le badge Publié/Brouillon change dans l'éditeur livret, et la page voyageur devient accessible ou non selon la valeur posée",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "publier rend le livret ouvrable par qui a le lien : ce qui a été lu pendant la publication ne se reprend pas",
    ne_fait_pas:
      "il ne change AUCUN contenu et n'envoie le lien à personne : il ouvre ou ferme la porte. Et dépublier ne referme pas le verrou d'accès d'une réservation — c'est un autre geste.",
    apercu: {
      lecture: "livret_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Publie le livret du Cocon",
      "Retire ce livret d'accueil, il n'est pas prêt",
      "Mets le livret en ligne pour ce logement",
    ],
    vague: 1,
    motif_requis: false,
    autonome: false,
    marquages: ["sortant"],
  },
  livret_config_editer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_config_upsert",
    methode: "PATCH",
    // Tout LIVRET_CONFIG_PATCHABLE_FIELDS (lib/livret-validation.js) sauf
    // `published` (son propre levier ci-dessus). Reste un mélange texte
    // (title, welcome_subtitle, hours, address_full…) et réglages (couleurs,
    // GYG, wifi, réseaux) — le texte suffit à classer tout l'ensemble en 9.
    args_autorises: [
      "logement_id",
      "slug",
      "type",
      "title",
      "welcome_subtitle",
      "contact_email",
      "contact_phone",
      "hours",
      "address_full",
      "city",
      "postal_code",
      "country",
      "google_maps_url",
      "wifi_networks",
      "website_url",
      "social_networks",
      "weather_lat",
      "weather_lng",
      "weather_city",
      "getyourguide_enabled",
      "gyg_location_id",
      "gyg_number_of_items",
      "reviews_threshold",
      "logo_url",
      "theme_color_primary",
      "category_order",
      "timezone",
      "default_language",
      "available_languages",
      "emergency_contact",
    ],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      type: ["location_courte_duree", "camping", "hotel", "autre"],
    },
    titre: "Modifier le contenu d'un livret",
    constatable:
      "les champs modifiés apparaissent dans l'éditeur livret du logement, et sur la page voyageur si le livret est publié — les langues vides se traduisent automatiquement au save, jamais celles déjà remplies",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le texte remplacé n'est gardé nulle part : la configuration du livret ne porte aucun historique de version",
    // ⚠️ LA RÈGLE DE L'AUTO-TRADUCTION EST UNE CONFUSION CLASSIQUE (ADR-200).
    ne_fait_pas:
      "les traductions EN/ES ne sont PAS refaites : l'auto-traduction au save ne remplit que les langues VIDES, jamais une langue déjà écrite. Et si le livret est publié, le changement est visible du voyageur au prochain chargement de sa page.",
    apercu: {
      lecture: "livret_config_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Change le titre du livret d'accueil",
      "Corrige l'adresse affichée dans le livret",
      "Mets le bon numéro de contact sur le livret",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  // ── Modules du livret (livret_modules) ────────────────────────────────
  //
  // 3 gestes déclarés (add / éditer texte / basculer visible / réordonner).
  // Le DELETE (`handleLivretModuleDelete`) est un DELETE SQL + nettoyage
  // Storage des photos liées — E1, exclu, cf. rapport.
  livret_module_add: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_module_upsert",
    methode: "POST",
    args_autorises: [
      "logement_id",
      "category",
      "subcategory",
      "content_type",
      "title",
      "content",
      "icon",
      "order_index",
      "enabled",
      "photo_urls",
      "video_url",
      "visibility_conditions",
    ],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ Tableau d'IDENTIFIANTS. Sans cette déclaration, le socle refusait le
    // tableau et le geste ne se composait jamais — trouvé en balayant avant
    // d'ouvrir la vague 3, pas en attendant l'échec (2026-09-09).
    args_formes: {
      photo_urls: { liste: true, max: 100 },
    },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      category: ["informations", "guide_appareils", "autre"],
    },
    titre: "Ajouter un module au livret",
    constatable:
      "la nouvelle rubrique apparaît dans le livret du logement — le voyageur ne la verra que si elle est affichée ET le livret publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // on AJOUTE : rien n'est écrasé
    ne_fait_pas:
      "il ne traduit rien tout seul au-delà des langues vides, et il n'ajoute aucune photo : le module naît avec le texte fourni, et rien d'autre",
    apercu: {
      lecture: "livret_module_lire",
      depend_de: ["logement_id", "category"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Ajoute une rubrique au livret d'accueil",
      "Il manque une page sur le parking, ajoute-la",
      "Crée une nouvelle rubrique dans le livret",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_module_editer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_module_upsert",
    methode: "PATCH",
    args_autorises: [
      "id",
      "category",
      "subcategory",
      "content_type",
      "title",
      "content",
      "icon",
      "order_index",
      "photo_urls",
      "video_url",
      "visibility_conditions",
    ],
    // ⚠️ Tableau d'IDENTIFIANTS. Sans cette déclaration, le socle refusait le
    // tableau et le geste ne se composait jamais — trouvé en balayant avant
    // d'ouvrir la vague 3, pas en attendant l'échec (2026-09-09).
    args_formes: {
      photo_urls: { liste: true, max: 100 },
    },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      category: ["informations", "guide_appareils", "autre"],
    },
    // « Réécris la rubrique Stationnement du Cocon Naturel » : le titre ET le
    // logement, parce que « Instruction d'arrivée » existe sur les onze
    // livrets (lot 2b, lib/livret-repere.js).
    args_resolus: {
      id: { objet: "module_livret", par: ["nom", "logement"] },
    },
    titre: "Modifier le texte d'un module du livret",
    // ⚠️ LA TRADUCTION N'A PAS LIEU PAR CE CHEMIN — vérifié dans le handler
    // le 2026-09-22 (revue métier du lot 2b) : l'auto-traduction au save
    // (ADR-200) vit dans l'ÉCRAN d'édition, pas dans `livret_module_upsert`.
    // Le texte d'avant disait le contraire ; Dimitri aurait cru que son
    // français réécrit se propageait à l'anglais que lit le voyageur.
    constatable:
      "le module modifié se met à jour dans l'éditeur livret et sur la page voyageur s'il est activé et le livret publié — dans la langue donnée seulement, l'anglais et l'espagnol déjà écrits ne bougent pas",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le texte remplacé n'est gardé nulle part : aucun module du livret ne porte d'historique de version",
    ne_fait_pas:
      "il ne traduit rien : la traduction automatique n'a lieu que depuis l'écran d'édition, jamais par ce geste — l'anglais et l'espagnol d'avant restent en ligne. Si le livret est publié, le voyageur verra le nouveau texte en rechargeant sa page.",
    apercu: {
      lecture: "livret_module_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Réécris la rubrique parking du livret",
      "Corrige ce texte dans le livret d'accueil",
      "Change ce qui est écrit sur la machine à laver",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_module_basculer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_module_upsert",
    methode: "PATCH",
    // Séparé de `livret_module_editer` : ici AUCUN texte, juste le booléen
    // affiché/masqué — ce qui le rend éligible à une vague bien plus tôt.
    args_autorises: ["id", "enabled"],
    args_resolus: {
      id: { objet: "module_livret", par: ["nom", "logement"] },
    },
    titre: "Afficher ou masquer un module du livret",
    constatable:
      "le module apparaît ou disparaît de la page voyageur (livret publié), et son état bascule dans l'éditeur admin",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // un booléen, rebasculable
    ne_fait_pas:
      "il n'efface aucun texte : le module masqué garde son contenu, il cesse seulement d'être affiché au voyageur",
    apercu: {
      lecture: "livret_module_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Cache cette rubrique du livret",
      "Affiche à nouveau cette rubrique",
      "Le voyageur ne doit pas voir cette page pour l'instant",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  // ── POI manuels d'alentours (section_type='alentours') ────────────────
  //
  // Le DELETE ici est LOGIQUE (`manual_deleted:true`) — hors E1, déclaré.
  livret_alentours_poi_toggle: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_alentours_poi_toggle",
    args_autorises: [
      "logement_id",
      "section_type",
      "key",
      "place_id",
      "tisseo_url",
      "manual_id",
      "flag",
      "value",
    ],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      flag: ["manual_pinned", "manual_excluded", "displayed_to_guest"],
      section_type: ["assistance", "alentours"],
    },
    // ⚠️ SIX ARGUMENTS, UN SEUL OBJET (lot 2b). « Épingle Fred the baker au
    // Cocon Naturel » : le lieu se retrouve par son nom dans TOUS les
    // secteurs du livret (38 noms, 38 distincts), et c'est lui qui donne le
    // secteur (`key`), le logement, la section, et SON identifiant dans
    // l'argument de SA source — `place_id` pour Google, `tisseo_url` pour
    // Tisséo, `manual_id` pour un ajout à la main (le handler cherche selon
    // `source` : un `manual_id` posé dans `place_id` répond 404). Le modèle
    // ne compose aucun des six ; il dit le nom et le logement.
    args_resolus: {
      logement_id: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
      section_type: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "section_type",
      },
      key: { objet: "lieu_livret", par: ["nom", "logement"], champ: "secteur" },
      place_id: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "place_id",
      },
      tisseo_url: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "tisseo_url",
      },
      manual_id: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "manual_id",
      },
    },
    titre: "Épingler ou masquer un lieu d'alentours",
    constatable:
      "le lieu change de statut (épinglé / exclu / affiché) dans l'éditeur Alentours et sur la page voyageur si affiché",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // trois drapeaux, tous rebasculables
    ne_fait_pas:
      "il ne supprime aucun lieu et n'en cherche aucun : il pose un drapeau (épinglé, exclu, affiché) sur un lieu que la génération a déjà trouvé",
    apercu: {
      lecture: "livret_alentours_lire",
      // ⚠️ LA CIBLE FAIT PARTIE DE L'APERÇU (revue adverse, 2026-09-16).
      // Sans elle, la carte rendait la LISTE du secteur/de la section et
      // laissait Dimitri deviner lequel allait changer — « enlève le contact
      // du médecin de garde » s'affichait « 12 contact(s) : … », sans jamais
      // nommer celui qui disparaît, et sans même le montrer s'il était le
      // neuvième. La lecture centre maintenant sa phrase sur la cible, et
      // rend un 404 franc si l'identifiant n'est plus là.
      depend_de: ["logement_id", "key", "place_id", "manual_id", "tisseo_url"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Épingle cette boulangerie dans les alentours",
      "Enlève ce restaurant des recommandations",
      "Ne montre plus ce lieu aux voyageurs",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },
  livret_alentours_poi_add_manual: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_alentours_poi_add_manual",
    // `poi` porte au moins `name` (texte que Dimitri écrit) et, en option,
    // adresse/téléphone/site — d'où la vague 9, comme toute création éditoriale.
    args_autorises: ["logement_id", "section_type", "key", "poi"],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      section_type: ["assistance", "alentours"],
    },
    // ⚠️ ICI LE NOM EST CELUI DU SECTEUR, PAS DU LIEU (lot 2b). Un lieu qu'on
    // AJOUTE n'existe pas encore : ce qui se désigne, c'est le secteur où il
    // va (« dans les restaurants », « dans les boulangeries »), et c'est lui
    // qui donne `key`. Le prompt le dit au modèle en région fixe.
    args_resolus: {
      logement_id: {
        objet: "secteur_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
      section_type: {
        objet: "secteur_livret",
        par: ["nom", "logement"],
        champ: "section_type",
      },
      key: { objet: "secteur_livret", par: ["nom", "logement"] },
    },
    titre: "Ajouter un lieu à la main dans les alentours",
    constatable:
      "le lieu ajouté apparaît dans le secteur choisi de l'éditeur Alentours, visible du voyageur si le livret est publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // retirable par le geste voisin (masquage, pas suppression)
    ne_fait_pas:
      "il ne vérifie l'existence de rien : le lieu est celui que Dimitri décrit, il n'est ni géocodé ni confronté à Google. Une adresse fausse partira telle quelle au voyageur.",
    apercu: {
      lecture: "livret_alentours_lire",
      depend_de: ["logement_id", "key"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Ajoute la boulangerie du coin dans les boulangeries du Cocon Naturel",
      "Il manque ce restaurant, mets-le dans les restaurants",
      "Rajoute une adresse à la main dans les supermarchés",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_alentours_poi_delete_manual: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_alentours_poi_delete_manual",
    args_autorises: [
      "logement_id",
      "section_type",
      "key",
      "manual_id_or_place_id",
    ],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      section_type: ["assistance", "alentours"],
    },
    // Le lieu par son nom (lot 2b) ; l'identifiant est celui de SA source, et
    // la garde refuse AVANT la carte un lieu venu de Google ou Tisséo — le
    // handler répond 400 dessus, et propose le masquage à la place.
    args_resolus: {
      logement_id: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
      section_type: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
        champ: "section_type",
      },
      key: { objet: "lieu_livret", par: ["nom", "logement"], champ: "secteur" },
      manual_id_or_place_id: {
        objet: "lieu_livret",
        par: ["nom", "logement"],
      },
    },
    titre: "Retirer un lieu manuel des alentours",
    constatable:
      "le lieu retiré disparaît de la page voyageur et passe en masqué dans l'éditeur Alentours (récupérable, ce n'est pas une suppression en base)",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le lieu redevient masqué, pas effacé — mais s'il avait été ajouté à la main, son nom et ses coordonnées ne réapparaissent dans aucune liste : il faudra les retaper",
    ne_fait_pas:
      "ce n'est PAS une suppression en base : le lieu passe en masqué et reste récupérable — malgré le mot « retirer » et le marquage « supprime »",
    apercu: {
      lecture: "livret_alentours_lire",
      // ⚠️ LA CIBLE FAIT PARTIE DE L'APERÇU (revue adverse, 2026-09-16).
      // Sans elle, la carte rendait la LISTE et laissait Dimitri deviner
      // lequel allait changer.
      depend_de: ["logement_id", "key", "manual_id_or_place_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Enlève ce lieu que j'avais ajouté",
      "Retire cette adresse des recommandations",
      "Ce commerce a fermé, sors-le de la liste",
    ],
    vague: 3,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime", "sortant"],
  },

  // ── Contacts d'assistance (medecin/pharmacie/hopital + contacts perso) ─
  //
  // Toutes ces routes acceptent POST (certaines aussi PATCH/DELETE en plus,
  // mais POST suffit toujours) — pas de note `methode` nécessaire ici.
  livret_assistance_contact_toggle: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_assistance_contact_toggle",
    // ⚠️ Le handler accepte POST **ou** PATCH sans les distinguer — il rejette
    // seulement les autres verbes. On déclare quand même POST : le test de
    // conformité exige que tout levier dont l'action mentionne PATCH dise son
    // verbe, et cette exigence n'est pas une formalité — elle force à ALLER
    // LIRE si les deux font la même chose. Ici oui ; sur
    // `livret_config_upsert`, non, et c'était un bouton mort.
    args_autorises: ["logement_id", "field", "value", "contact_id", "key"],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur — sans elles, le
    // modèle compose du plausible et la carte échoue sous le clic
    // (défaut vécu le 2026-09-09 : « adresse » pour « adresse_complete »).
    args_valeurs: {
      key: ["medecin_proche", "pharmacie_garde", "hopital_proche"],
      field: ["displayed_to_guest"],
    },
    // ⚠️ « la pharmacie de garde », « le médecin », « l'hôpital » — ou
    // l'enseigne (lot 2b). La clé est fermée à trois contacts engendrés, et
    // la section n'existe QUE sur lc1 : ailleurs, la garde dit « pas de
    // section assistance » avant la carte, au lieu du 404 sous le doigt.
    // `contact_id` (un contact ajouté à la main) reste un argument nu : aucun
    // n'existe en prod, et le levier qui les retire est sorti du lot.
    args_resolus: {
      logement_id: {
        objet: "contact_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
      key: { objet: "contact_livret", par: ["nom", "logement"] },
    },
    titre: "Afficher ou masquer un contact d'assistance",
    constatable:
      "le contact bascule visible/masqué dans l'éditeur Assistance et sur la page voyageur si affiché",
    methode: "POST",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // un booléen, rebasculable
    // ⚠️ CE SONT LES CONTACTS DE SANTÉ D'UN VOYAGEUR EN DIFFICULTÉ.
    ne_fait_pas:
      "il ne supprime aucun contact. Mais masquer un contact d'assistance retire un numéro que le voyageur pourrait chercher en urgence : le geste est réversible, pas anodin.",
    apercu: {
      lecture: "livret_assistance_lire",
      // ⚠️ LA CIBLE FAIT PARTIE DE L'APERÇU (revue adverse, 2026-09-16).
      // Sans elle, la carte rendait la LISTE et laissait Dimitri deviner
      // lequel allait changer.
      depend_de: ["logement_id", "contact_id", "key"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Cache ce contact d'urgence du livret",
      "Remets la pharmacie de garde en avant",
      "Ne montre plus ce numéro aux voyageurs",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },
  livret_assistance_contact_add: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_assistance_contact_add",
    // `contact` porte au moins type/name/phone (texte que Dimitri fournit) —
    // vague 9 comme tout ajout éditorial avec identité/coordonnées à saisir.
    args_autorises: ["logement_id", "contact"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Ajouter un contact d'assistance",
    constatable:
      "le contact ajouté apparaît dans la liste Assistance de l'éditeur, visible du voyageur si le livret est publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // retirable (suppression logique)
    ne_fait_pas:
      "il ne vérifie aucun numéro : le contact est celui que Dimitri fournit, et c'est ce numéro-là qu'un voyageur composera",
    apercu: {
      lecture: "livret_assistance_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Ajoute le numéro du médecin dans le livret",
      "Il manque un numéro d'urgence, ajoute-le",
      "Rajoute la pharmacie du quartier dans les secours",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_assistance_contact_update: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_assistance_contact_update",
    args_autorises: ["logement_id", "contact_id", "key", "contact"],
    // ⚠️ Liste fermée trouvée par le balayage qui SUIT LES HELPERS APPELÉS —
    // la validation ne vit pas dans le handler mais dans une fonction qu'il
    // appelle. Un balayage limité au corps du handler l'aurait manquée.
    args_valeurs: {
      key: ["medecin_proche", "pharmacie_garde", "hopital_proche"],
    },
    // Même repère que le masquage (lot 2b) : le rôle ou l'enseigne, plus le
    // logement — et « pas de section assistance » dit avant la carte.
    args_resolus: {
      logement_id: {
        objet: "contact_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
      key: { objet: "contact_livret", par: ["nom", "logement"] },
    },
    titre: "Modifier un contact d'assistance",
    constatable:
      "les coordonnées modifiées apparaissent dans l'éditeur Assistance et sur la page voyageur si affiché",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "les coordonnées remplacées ne sont gardées nulle part : l'ancien numéro ne revient pas",
    ne_fait_pas:
      "il ne prévient personne et ne vérifie aucun numéro : le voyageur composera ce qui est écrit là, tel quel",
    apercu: {
      lecture: "livret_assistance_lire",
      // ⚠️ LA CIBLE FAIT PARTIE DE L'APERÇU (revue adverse, 2026-09-16).
      // Sans elle, la carte rendait la LISTE et laissait Dimitri deviner
      // lequel allait changer.
      depend_de: ["logement_id", "contact_id", "key"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Le médecin a changé de numéro, corrige-le",
      "Mets à jour ce contact d'urgence",
      "Corrige les coordonnées de l'hôpital",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_assistance_contact_delete: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_assistance_contact_delete",
    // Réservé aux contacts `source==='manual'` par le handler lui-même —
    // suppression LOGIQUE (`manual_deleted`), pas de DELETE SQL. Hors E1.
    args_autorises: ["logement_id", "contact_id"],
    titre: "Retirer un contact d'assistance manuel",
    constatable:
      "le contact retiré disparaît de la page voyageur et passe en masqué dans l'éditeur Assistance (récupérable, ce n'est pas une suppression en base)",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le contact est masqué, pas effacé — mais c'était un contact ajouté à la main : son nom et son numéro ne réapparaissent dans aucune liste, il faudra les retaper",
    ne_fait_pas:
      "ce n'est PAS une suppression en base : le contact passe en masqué et reste récupérable — et le serveur refuse de toucher aux contacts générés, seuls ceux ajoutés à la main partent",
    apercu: {
      lecture: "livret_assistance_lire",
      // ⚠️ LA CIBLE FAIT PARTIE DE L'APERÇU (revue adverse, 2026-09-16).
      // Sans elle, la carte rendait la LISTE et laissait Dimitri deviner
      // lequel allait changer.
      depend_de: ["logement_id", "contact_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Enlève ce contact d'urgence que j'avais ajouté",
      "Retire ce numéro du livret",
      "Ce contact ne sert plus, sors-le",
    ],
    vague: 3,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime", "sortant"],
  },

  // ── Catégories du livret (livret_categories) ───────────────────────────
  //
  // ⚠️ `livret_category_delete` — ÉCARTÉ AU PREMIER PASSAGE (« DELETE SQL, E1 »),
  // DÉCLARÉ LE 2026-09-12 APRÈS RELECTURE DU HANDLER. La règle d'E1 vise « rien
  // qui détruit SANS RETOUR » ; ici, ce qui part est un libellé que Dimitri a
  // tapé et peut retaper — `livret_category_create` est déjà déclaré juste en
  // dessous, le chemin de retour existe et il est nommé. Et le handler refuse
  // tout seul les deux cas où la perte déborderait : une catégorie SYSTÈME
  // (409 `cannot_delete_system_category`) et une catégorie encore UTILISÉE par
  // des modules (409 `category_has_modules`, avec le compte). Il ne peut donc
  // emporter qu'une étiquette vide.
  // C'est la doctrine de Dimitri appliquée telle quelle : « il propose, je
  // clique — ça protège mieux qu'une interdiction, parce que je vois ce qui va
  // se passer avant que ça se passe ». Le marquage `supprime` est là pour que
  // la carte NOMME ce qui part.
  // `_create`/`_update`/`_reorder` restent : create pose des libellés que
  // Dimitri écrit (vague 9) ; update de même ; reorder ne touche aucun texte.
  livret_category_delete: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_category_delete",
    args_autorises: ["id", "logement_id"],
    // « Supprime la catégorie Bonnes adresses du Cocon Naturel » (lot 2b).
    // La lecture passe en service_role — `livret_categories` n'a aucune
    // policy pour le jeton (revue M2) — bornée au périmètre en code. Et les
    // trois refus du handler (409 d'origine, 409 encore pleine) plus un
    // quatrième (la dernière) se disent AVANT la carte, avec le nombre.
    args_resolus: {
      id: { objet: "categorie_livret", par: ["nom", "logement"] },
      logement_id: {
        objet: "categorie_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
    },
    titre: "Supprimer une catégorie du livret",
    constatable:
      "la catégorie disparaît du livret du logement et du sommaire que lit le voyageur ; ses noms en français, anglais et espagnol sont perdus et devront être retapés pour la recréer — le geste est refusé si elle est d'origine, ou si des rubriques s'en servent encore",
    // Vague 2 « rangement et clôtures » : c'est du rangement d'éditeur, pas de
    // l'éditorial (aucun texte à écrire ici, seulement un id à désigner).
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "les libellés FR/EN/ES de la catégorie sont perdus : il faudra les retaper pour la recréer",
    ne_fait_pas:
      "il ne supprime aucun module : le serveur refuse tant qu'un module l'utilise, et refuse aussi sur une catégorie système",
    apercu: {
      lecture: "livret_categories_lire",
      depend_de: ["logement_id", "id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Supprime cette catégorie du livret",
      "Enlève cette catégorie, elle est vide",
      "Ce classement ne me sert plus, retire-le",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime", "sortant"],
  },
  // ══ RÉORDONNER — LES CINQ GESTES FERMÉS LE 2026-09-22 (D3) ═══════════════
  //
  // LES QUATRE `*_reorder` DU LIVRET ONT ÉTÉ DÉCLARÉS ICI ET SONT MAINTENANT ÉCARTÉS
  // (`hors_atteinte`, lib/operateur-leviers.js). Le motif n'est pas le risque
  // — un ordre d'affichage n'en porte aucun — c'est qu'AUCUN REPÈRE PARLÉ NE
  // PRODUIT UNE LISTE ENTIÈRE. L'argument est un tableau d'identifiants dans
  // l'ordre voulu ; Dimitri dit « fais remonter le wifi en premier », jamais
  // les huit identifiants d'une catégorie dans l'ordre. C'est exactement le
  // défaut de `logement_gallery_set_cover` : une ligne de catalogue pour un
  // geste qui ne peut jamais s'armer.
  //
  // ⚠️ ET MÊME UN « METS-LE EN PREMIER » N'AURAIT PAS D'ORDRE DE DÉPART :
  // `ordreEnregistre()` (lib/livret-reperes.js) rend FAUX sur ce parc — les
  // treize modules sont tous à `order_index = 0`. Le déplacement RELATIF est
  // un lot à part (2c), et il commence par ranger quelque chose.

  livret_category_create: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_category_create",
    // ⚠️ `slug` AVANT `logement_id`, et c'est l'ordre du jeton au catalogue
    // (`vise=nom+logement`, comme les dix autres du lot) — le premier
    // argument résolu écrit le jeton.
    args_autorises: ["slug", "logement_id", "label_fr", "label_en", "label_es"],
    // ⚠️ UNE CRÉATION NOMME CE QU'ELLE CRÉE (lot 2b, même règle que
    // `menage_instruction_save`) : `requis: []` laisse passer l'absence, et le
    // SLUG est alors DÉRIVÉ du nom par le code (« Bonnes adresses » →
    // `bonnes_adresses`, la forme que le handler exige) — pas composé par le
    // modèle, qui écrirait « bonnes-adresses » et échouerait sous le doigt.
    // Un nom qui existe déjà refuse : rien à créer, on propose de renommer.
    args_resolus: {
      slug: {
        objet: "categorie_livret",
        par: ["nom", "logement"],
        requis: [],
      },
      logement_id: { objet: "logement", par: ["logement"] },
    },
    titre: "Créer une catégorie dans le livret",
    constatable:
      "la nouvelle catégorie apparaît dans le livret du logement, et dans le sommaire que le voyageur lit dès qu'une rubrique y sera rangée",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // supprimable ensuite, tant qu'aucun module ne l'utilise
    ne_fait_pas:
      "elle naît VIDE et n'attire aucun module : il faudra en déplacer ou en créer pour qu'elle serve à quelque chose",
    apercu: {
      lecture: "livret_categories_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Crée une catégorie Bonnes adresses dans le livret",
      "Ajoute une catégorie au livret d'accueil",
      "Il me faut un nouveau classement de sections",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },
  livret_category_update: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_category_update",
    args_autorises: ["id", "logement_id", "label_fr", "label_en", "label_es"],
    args_resolus: {
      id: { objet: "categorie_livret", par: ["nom", "logement"] },
      logement_id: {
        objet: "categorie_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
    },
    titre: "Renommer une catégorie du livret",
    constatable:
      "le nouveau libellé de la catégorie apparaît dans l'éditeur livret et partout où elle est affichée au voyageur",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "les libellés remplacés ne sont gardés nulle part : pour revenir en arrière, il faudra les retaper",
    ne_fait_pas:
      "il ne déplace aucun module et ne change pas l'identifiant de la catégorie : seul le libellé lu par le voyageur change",
    apercu: {
      lecture: "livret_categories_lire",
      depend_de: ["logement_id", "id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Renomme cette catégorie du livret",
      "Change le nom de cette catégorie",
      "Appelle cette catégorie Pratique plutôt que Divers",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  // ── FAQ du livret (livret_faqs) ─────────────────────────────────────────
  //
  // ⚠️ `livret_faq_delete` — ÉCARTÉ AU PREMIER PASSAGE (« DELETE SQL, E1 »),
  // DÉCLARÉ LE 2026-09-12, même raisonnement que la catégorie ci-dessus : ce
  // qui part est du TEXTE que Dimitri a écrit et peut réécrire, et le chemin de
  // retour (`livret_faq_upsert`) est déclaré dans ce même fichier. Aucun
  // fichier, aucun Storage, aucun tiers — `handleLivretFaqDelete` fait un
  // `.delete().eq('id')` et rien d'autre.
  // ⚠️ MAIS LE TEXTE, LUI, NE REVIENT PAS TOUT SEUL : trois langues écrites à
  // la main disparaissent d'un clic. D'où `supprime`, et un `constatable` qui
  // le dit avant — pas un titre qui dit « supprimer » et laisse découvrir quoi.
  // ⚠️ Le handler ne lit QUE `body.id` (pas de `logement_id`) : la carte doit
  // donc désigner la bonne FAQ du premier coup, il n'y a pas de garde de
  // périmètre derrière. C'est pourquoi `logement_id` n'est PAS dans la liste —
  // un argument que le serveur ignore donnerait l'illusion d'un garde-fou.
  livret_faq_delete: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_faq_delete",
    // 405 sur tout verbe ≠ POST.
    methode: "POST",
    args_autorises: ["id"],
    titre: "Supprimer une question de la FAQ",
    constatable:
      "la question et sa réponse disparaissent de l'éditeur FAQ du logement et de la page voyageur ; leur texte dans les trois langues est perdu et devra être réécrit pour la recréer",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le texte de la question et de la réponse, dans les trois langues, est perdu : il faudra le réécrire pour la recréer",
    ne_fait_pas:
      "il ne touche pas aux questions apprises du copilote, qui vivent ailleurs : c'est la FAQ que LE VOYAGEUR lit dans le livret",
    apercu: {
      lecture: "livret_faq_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Enlève cette question du livret d'accueil",
      "Supprime cette question fréquente",
      "Cette question ne se pose plus, retire-la",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    marquages: ["supprime", "sortant"],
  },
  // ⚠️ `livret_faq_upsert` — CE LEVIER NE FAIT QUE CRÉER (2026-09-09).
  // Il déclarait « POST ou PATCH (PATCH si `id` fourni) », ce qui n'est pas
  // une méthode mais une phrase : l'exécution envoie UN verbe, elle ne lit pas
  // une condition en prose. Et le handler choisit sur `req.method`, jamais sur
  // la présence d'`id` — un POST avec `id` aurait donc créé un doublon en
  // silence, là où la carte annonçait une modification.
  // `id` est retiré des arguments autorisés : ce qui n'est pas exécutable ne
  // doit pas être composable. Modifier une FAQ reste un geste à déclarer, avec
  // sa propre entrée en PATCH — c'est un ajout, pas un correctif.

  livret_faq_upsert: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_faq_upsert",
    // POST (création, sans `id`) ou PATCH (édition, avec `id`) — le handler
    // route sur `req.method`, POST fonctionne pour les deux à l'exception du
    // routage id ; on documente PATCH car c'est la note qui manque le plus
    // souvent côté exécuteur.
    methode: "POST",
    args_autorises: ["logement_id", "question", "answer", "order_index"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ Objet MULTILINGUE : clés fermées (les trois langues du livret),
    // contenu LIBRE — c'est Dimitri qui écrit ces textes, d'où la vague 9.
    // Sans cette déclaration, le socle refusait l'objet et le geste ne se
    // composait JAMAIS : un bouton invisible plutôt que mort (2026-09-09).
    args_formes: {
      question: { cles: ["fr", "en", "es"], texte: true },
      answer: { cles: ["fr", "en", "es"], texte: true },
    },
    titre: "Ajouter ou modifier une question de la FAQ",
    constatable:
      "la question/réponse apparaît (ou se met à jour) dans l'éditeur FAQ du logement et sur la page voyageur si le livret est publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // POST seul : ce levier CRÉE, il n'écrase rien
    ne_fait_pas:
      "il ne MODIFIE pas, malgré son titre : il CRÉE. Corriger une question déjà écrite reste un geste à monter — viser une question existante créerait ici un doublon en silence, c'est pourquoi on ne peut pas la viser.",
    apercu: {
      lecture: "livret_faq_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Ajoute une question fréquente au livret",
      "Mets la question du parking dans les questions fréquentes",
      "Crée une question-réponse pour les voyageurs",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  // ── Photo de couverture d'un module (livret_module_photos) ─────────────
  //
  // ⚠️ `livret_module_photo_delete` — ÉCARTÉ (E1), ET LA LIGNE DE PARTAGE AVEC
  // LES DEUX SUPPRESSIONS DÉCLARÉES CI-DESSUS EST CELLE-CI : ce qui part est un
  // FICHIER, pas un texte. Une catégorie ou une FAQ, Dimitri les retape ; une
  // photo effacée du bucket `livret-photos`, il faut la retrouver ou la
  // reprendre. Le handler est pourtant irréprochable (triple contrôle
  // photo → module → logement, anti-IDOR, SQL d'abord puis Storage) : ce n'est
  // pas sa qualité qui le tient dehors, c'est la nature de ce qu'il détruit.
  //
  // ⚠️ TENSION ASSUMÉE ET REMONTÉE, PAS MASQUÉE : `logement_gallery_delete`
  // (`lib/leviers/logements.js`) est le MÊME geste sur la galerie du logement,
  // et il EST déclaré avec le marquage `supprime` — sa propre note dit que la
  // tension avec E1 était assumée. Les deux ne peuvent pas rester durablement
  // de part et d'autre de la ligne. On ne tranche pas ici en élargissant sur
  // une lancée : c'est un arbitrage de Dimitri (« la photo, je la perds
  // vraiment ? »), et il vaut pour les deux d'un coup.
  //
  // ⚠️ `livret_photo_delete` — ÉCARTÉ, et pour une raison DIFFÉRENTE de celle
  // qu'on lui prêterait. Son seul argument est un `storage_path` BRUT :
  // `handleLivretPhotoDelete` ne lit aucune ligne, ne vérifie aucune
  // appartenance à un logement, ne touche AUCUNE table — il appelle
  // `storage.remove([path])` et s'arrête là. Donc (1) rien à constater : la
  // ligne qui référence l'image reste en base, et ce que Dimitri verrait après
  // son clic, c'est une image cassée, pas un changement — E2 tombe ; (2) rien
  // ne borne la cible : le chemin est le seul garde-fou, et il vient du modèle.
  // C'est l'inverse exact d'une suppression désignée par son id.
  //
  // ⚠️ `livret_sections_auto_photo_upload_url` — ÉCARTÉ. Il ne supprime ni
  // n'écrit rien : il rend une URL d'upload SIGNÉE, première moitié d'un geste
  // dont la seconde est un PUT de fichier binaire. L'opérateur ne détient aucun
  // fichier — composer cette intention produirait une URL que personne
  // n'utilise, et une carte dont le `constatable` serait « rien ne change ».
  // E2, à la lettre.
  //
  // ⚠️ `livret_module_unlock` — ÉCARTÉ, et le nom trompe : ce n'est pas un
  // geste d'admin. Il est dans `ANON_ACTIONS` (`api/beds24-mapping.js`) parce
  // que c'est LE VOYAGEUR qui l'exécute, depuis sa page, en tapant les quatre
  // derniers chiffres de son téléphone pour prouver que c'est bien lui
  // (ADR-172). Deux choses en découlent : composer ces quatre chiffres, c'est
  // affirmer une identité À LA PLACE du voyageur (E4) ; et chaque tentative
  // ratée consomme le compteur de lockout côté base — un geste de travers
  // verrouillerait l'accès du vrai voyageur à son propre module.
  // `_upsert` n'UPLOAD rien lui-même (l'upload binaire précède, via une URL
  // signée hors du périmètre d'un geste composable) : il ENREGISTRE une photo
  // DÉJÀ hébergée — utile par exemple pour reprendre l'URL publique d'une
  // photo déjà en ligne ailleurs sur un autre module/logement.
  livret_module_photo_upsert: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_module_photo_upsert",
    args_autorises: [
      "module_id",
      "logement_id",
      "storage_path",
      "public_url",
      "alt_text",
      "order_index",
    ],
    // ⚠️ Objet MULTILINGUE : clés fermées (les trois langues du livret),
    // contenu LIBRE — c'est Dimitri qui écrit ces textes, d'où la vague 9.
    // Sans cette déclaration, le socle refusait l'objet et le geste ne se
    // composait JAMAIS : un bouton invisible plutôt que mort (2026-09-09).
    args_formes: {
      alt_text: { cles: ["fr", "en", "es"], texte: true },
    },
    // La rubrique par son titre et son logement (lot 2b) ; `logement_id` est
    // celui de la rubrique trouvée — le handler vérifie qu'ils vont ensemble.
    args_resolus: {
      module_id: { objet: "module_livret", par: ["nom", "logement"] },
      logement_id: {
        objet: "module_livret",
        par: ["nom", "logement"],
        champ: "logement_id",
      },
    },
    titre: "Attacher une photo à un module du livret",
    constatable:
      "la photo apparaît en couverture du module dans l'éditeur livret et sur la page voyageur si le module est activé et le livret publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // la photo précédente reste dans le Storage
    // ⚠️ IL NE TÉLÉVERSE RIEN : le fichier doit déjà être dans le Storage.
    ne_fait_pas:
      "il ne téléverse aucune image : il ATTACHE un fichier déjà présent dans le Storage (l'opérateur ne détient aucun binaire, et l'envoi d'un fichier n'est pas un geste composable)",
    apercu: {
      lecture: "livret_module_lire",
      depend_de: ["module_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Mets cette image en tête de la rubrique",
      "Attache une photo à cette section du livret",
      "Ajoute l'illustration sur cette page du livret",
    ],
    marquages: ["sortant"],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  // ── Sections auto-générées (livret_sections_auto : alentours, assistance) ─
  //
  // `livret_sections_auto_delete` est un DELETE SQL de toute la section —
  // E1, exclu (régénérer ne restaure pas les réglages manuels perdus, ce
  // n'est pas une suppression logique).
  livret_sections_auto_toggle: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_sections_auto_toggle",
    args_autorises: ["logement_id", "section_type", "enabled"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      section_type: ["assistance", "alentours"],
    },
    titre: "Activer ou couper une section auto du livret",
    constatable:
      "la section (alentours ou assistance) apparaît ou disparaît de la page voyageur si le livret est publié",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // un booléen, rebasculable
    ne_fait_pas:
      "il ne génère ni n'efface aucun contenu : la section garde ce qu'elle contient, elle cesse (ou reprend) d'être affichée au voyageur",
    apercu: {
      lecture: "livret_sections_auto_lire",
      depend_de: ["logement_id", "section_type"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Affiche les alentours dans le livret de ce logement",
      "Cache la partie assistance du livret",
      "N'affiche plus les recommandations du quartier",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },
  // ⚠️ `livret_sections_auto_upsert` A ÉTÉ RETIRÉ (2026-09-09, à l'intégration).
  // Vérifié sur le code, pas sur le nom : ce handler écrit `content` SEUL,
  // là où `livret_sections_auto_generate` écrit la même ligne avec `enabled`
  // ET `last_generated_at`. C'est le motif littéral de l'exclusion E3 — un
  // écrivain PARTIEL d'une ligne qu'un autre complète (le sosie de #1327).
  // Conséquence concrète : après un upsert, l'écran continue d'afficher la
  // date de génération d'AVANT, sur un contenu qui a changé. Rien ne plante,
  // et la date est fausse.

  livret_sections_auto_generate: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_sections_auto_generate",
    // Nommé `type` dans le body de CE handler (pas `section_type`, cf. code).
    args_autorises: ["logement_id", "type"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      type: ["assistance", "alentours"],
    },
    titre: "(Re)générer une section du livret par IA",
    constatable:
      "le contenu régénéré de la section (alentours ou assistance) remplace l'ancien dans l'éditeur livret et sur la page voyageur — les réglages manuels précédents (épinglages, exclusions) ne sont pas garantis survivre à une régénération",
    sujet: "livret",
    onglet: "livret",
    // ⚠️ LE SEUL GESTE DU REGISTRE QUI COÛTE DE L'ARGENT SANS TOUCHER À
    // L'ARGENT : il appelle le modèle (facturé au jeton) et Google (facturé à
    // la requête). Ce n'est pas le marquage `argent` — aucun montant ne change
    // dans l'app — mais c'est une dépense, et elle doit se lire.
    cout: ["appel_ia", "appel_google"],
    ou_ca_part: ["page_publique"],
    sans_retour:
      "le contenu régénéré remplace l'ancien, qui n'est gardé nulle part — et les épinglages et exclusions posés à la main ne survivent pas forcément",
    ne_fait_pas:
      "il ne demande rien avant d'écrire : il REMPLACE la section en place. Ce n'est pas un brouillon qu'on relit — le voyageur verra le nouveau texte dès la régénération si le livret est publié.",
    apercu: {
      lecture: "livret_sections_auto_lire",
      depend_de: ["logement_id", "type"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Regénère les alentours du Cocon",
      "Refais la liste des bonnes adresses de ce logement",
      "Remets à jour les recommandations du quartier",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  // ── Pré-remplissage guide des appareils (livret_modules, bulk insert) ───
  livret_prefill_appareils: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_prefill_appareils",
    // Contenu fixe côté serveur (9 appareils standards, titres/icônes figés,
    // `content` vide, `enabled:false`) : aucun texte à fournir, d'où la vague
    // basse malgré la création de 9 modules d'un coup.
    args_autorises: ["logement_id"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Pré-remplir le guide des appareils",
    constatable:
      "9 modules 'guide appareils' apparaissent en BROUILLON (désactivés) dans l'éditeur livret du logement, invisibles du voyageur tant qu'ils ne sont pas activés",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // les 9 modules naissent désactivés et se masquent
    ne_fait_pas:
      "rien n'atteint le voyageur : les neuf modules naissent en BROUILLON, désactivés et vides. Et il n'y a pas de geste inverse — les retirer se fait un par un, à l'écran.",
    apercu: {
      lecture: "livret_module_lire",
      depend_de: ["logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Prépare le guide des appareils de ce logement",
      "Mets en place les notices d'électroménager",
      "Crée les pages appareils du livret",
    ],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  // ── Copier du contenu d'un logement vers un autre ──────────────────────
  livret_copy_items: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_copy_items",
    // Mode AJOUT PUR (ADR-204) : INSERT id neuf, jamais d'UPDATE, la source et
    // l'existant de la cible restent intacts. Photos non copiées (v1).
    args_autorises: [
      "source_logement_id",
      "target_logement_id",
      "module_ids",
      "faq_ids",
    ],
    // ⚠️ Tableau d'IDENTIFIANTS. Sans cette déclaration, le socle refusait le
    // tableau et le geste ne se composait jamais — trouvé en balayant avant
    // d'ouvrir la vague 3, pas en attendant l'échec (2026-09-09).
    args_formes: {
      module_ids: { liste: true, max: 100 },
      faq_ids: { liste: true, max: 100 },
    },
    titre: "Copier des modules ou FAQ entre logements",
    constatable:
      "les rubriques et les questions copiées s'ajoutent en fin de liste dans le livret du logement d'arrivée — et le voyageur de ce logement les lira dès que le livret sera publié ; rien n'est retiré ni écrasé, ni au départ ni à l'arrivée",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    ou_ca_part: ["page_publique"],
    sans_retour: null, // AJOUT PUR : rien n'est retiré ni écrasé
    ne_fait_pas:
      "il n'écrase RIEN, ni à la source ni à la cible : les éléments copiés s'ajoutent en fin de liste, avec des identifiants neufs. Les photos ne suivent PAS (v1), et rien n'est retraduit.",
    apercu: {
      lecture: "livret_module_lire",
      // ⚠️ LE LOGEMENT DE DÉPART AUSSI (revue #1487). La carte masque les
      // identifiants : sans son nom rendu par la lecture, « Récupérer depuis
      // X » ne nommait plus ni le départ ni l'arrivée. La lecture porte sur
      // la CIBLE (ses rubriques actuelles) ; la source n'est là que pour être
      // NOMMÉE — rien n'est lu de son livret.
      depend_de: ["target_logement_id", "source_logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Copie les rubriques du Cocon vers le Nid",
      "Reprends le livret de ce logement pour l'autre",
      "Duplique ces rubriques sur un autre logement",
    ],
    marquages: ["sortant"],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  // ── Dérogation manuelle au verrou du livret (reservations/pricing_blocks) ─
  //
  // 🔴 Ouvre les DEUX étages du verrou voyageur : l'accès au livret ET le
  // module « instructions d'arrivée » — donc le CODE D'ACCÈS AU LOGEMENT,
  // avant toute preuve d'identité autre que les 4 derniers chiffres du
  // téléphone déjà exigés à l'écran. Réversible (`active:false` referme),
  // mais l'effet EST que le code de la porte part vers un voyageur — marquage
  // `sortant`. Motif obligatoire À LA POSE (barrière serveur, 400 sinon) :
  // c'est un texte que Dimitri écrit sur les circonstances → vague 9.
  livret_override_set: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "livret_override_set",
    args_autorises: ["reservation_id", "active", "reason"],
    // ⚠️ DIMITRI NE PRONONCE PAS D'IDENTIFIANT DE SÉJOUR, ET LE MODÈLE NE L'A
    // VU NULLE PART (le relevé lit les réservations sans leur `id`,
    // `sejours_periode` ne le rend pas). S'il en composait un, il l'aurait
    // inventé — et derrière, c'est le CODE D'ENTRÉE d'un logement qui s'ouvre
    // pour le mauvais voyageur. Le repère se résout ici, au serveur
    // (`lib/resa-repere.js`), et il cherche dans les DEUX tables, comme
    // `_resolveTarget` de ce handler.
    args_resolus: {
      reservation_id: {
        objet: "reservation",
        // ⚠️ `sens` ET `rang` SONT LES CANAUX DE RÉPONSE À « LAQUELLE ? ». Ils
        // ne sont pas requis (`requis` ne garde que les deux premiers) : ils
        // n'existent qu'au second tour, quand la question a listé des
        // candidats. Sans eux, « celle qui arrive » reposait la même question.
        par: ["logement", "quand", "sens", "rang"],
        requis: ["logement", "quand"],
      },
    },
    titre: "Débloquer l'accès au livret d'une réservation",
    constatable:
      "la réservation affiche « accès ouvert à la main » dans l'écran Livret, avec la raison et le nom de qui l'a ouvert — et le voyageur accède aussitôt au livret ET au code d'entrée du logement",
    sujet: "livret",
    onglet: "livret",
    cout: ["rien"],
    // ⚠️ CE QUI PART EST LE CODE DE LA PORTE. Le geste n'envoie aucun message,
    // mais il ouvre au voyageur l'accès au livret ET aux instructions
    // d'arrivée — donc au code d'accès du logement.
    ou_ca_part: ["atteignable"],
    sans_retour:
      "refermer ne reprend pas ce qui a été lu : si le voyageur a ouvert le livret, il a le code d'entrée, et il le garde",
    ne_fait_pas:
      "il n'envoie aucun message et ne prévient pas le voyageur : il lève le verrou. C'est à lui d'ouvrir le lien — et s'il l'ouvre, il voit le code d'accès du logement.",
    apercu_absent:
      "aucune lecture ne rend l'état du verrou ni la réservation visée (quel voyageur, quelles dates, pourquoi il est bloqué) : il faudrait `livret_override_lire`. Ouvrir l'accès au code d'une porte sur un identifiant nu est le manque le plus grave de cette famille.",
    dit_comme: [
      "Donne l'accès au livret à ce voyageur tout de suite",
      "Débloque le code d'arrivée pour cette réservation",
      "Laisse-le voir les instructions maintenant",
    ],
    vague: 9,
    motif_requis: true,
    // Le nom sous lequel CE handler attend le motif. Sans lui, celui que
    // Dimitri écrit part dans le vide (2026-09-09).
    motif_champ: "reason",
    autonome: false,
    marquages: ["sortant"],
  },
});

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "livret_override_set.active": {
    oui: "Lever le verrou",
    non: "Remettre le verrou",
  },
  "livret_config_publier.published": {
    oui: "Publier le livret",
    non: "Retirer le livret",
  },
  "livret_config_editer.getyourguide_enabled": {
    oui: "Afficher les activités GetYourGuide",
    non: "Masquer les activités GetYourGuide",
  },
  "livret_module_add.enabled": {
    oui: "Montrer la rubrique au voyageur",
    non: "Garder la rubrique masquée",
  },
  "livret_module_basculer.enabled": {
    oui: "Afficher la rubrique",
    non: "Masquer la rubrique",
  },
  "livret_sections_auto_toggle.enabled": {
    oui: "Afficher la section",
    non: "Masquer la section",
  },
  "livret_alentours_poi_toggle.value": {
    oui: "Poser le marquage",
    non: "Retirer le marquage",
  },
  "livret_assistance_contact_toggle.value": {
    oui: "Afficher le contact",
    non: "Masquer le contact",
  },
});

module.exports = { LEVIERS_LIVRET, BOOLEENS_LIBELLES };
